// src/components/common/bulk/FileUploadZone.jsx
import React, { useRef, useState } from 'react';
import { Upload, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './FileUploadZone.module.css';

const FileUploadZone = ({ uploadedFile, onFileUpload, disabled }) => {
  const { t } = useLocalization();
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);

  const validateFile = (file) => {
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setError(t('common.bulkInvalidFileFormat'));
      return false;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return false;
    }

    setError(null);
    return true;
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    if (validateFile(file)) {
      try {
        await onFileUpload(file);
      } catch (err) {
        setError(err.message || t('common.bulkFileParsingError'));
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  if (uploadedFile) {
    return (
      <div className={styles.fileUploadZone}>
        <div className={styles.uploadedFile}>
          <div className={styles.fileIcon}>
            <FileSpreadsheet size={48} />
          </div>
          <div className={styles.fileInfo}>
            <div className={styles.fileName}>{uploadedFile.name}</div>
            <div className={styles.fileSize}>
              {(uploadedFile.size / 1024).toFixed(2)} KB
            </div>
          </div>
          <div className={styles.fileStatus}>
            <span className={styles.successBadge}>
              {t('common.bulkFileUploaded')}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.fileUploadZone}>
      <div
        className={`${styles.dropZone} ${isDragging ? styles.dragging : ''} ${disabled ? styles.disabled : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileInputChange}
          className={styles.fileInput}
          disabled={disabled}
        />

        <Upload className={styles.uploadIcon} size={48} />
        <h3>{t('common.bulkDragDropFile')}</h3>
        <p>{t('common.bulkOrClickToSelect')}</p>
        <span className={styles.supportedFormats}>
          {t('common.bulkSupportedFormats')}
        </span>
      </div>

      {error && (
        <div className={styles.error}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;
